import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

type Props = {
    children: React.ReactNode;
};

const HorizontalScrollList = ({ children }: Props) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollButtons = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollButtons();
        el.addEventListener('scroll', updateScrollButtons);
        window.addEventListener('resize', updateScrollButtons);
        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, [updateScrollButtons]);

    // Re-check when children change (e.g. lists finish loading)
    useEffect(() => {
        updateScrollButtons();
    }, [children, updateScrollButtons]);

    const scroll = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction === 'left' ? -150 : 150, behavior: 'smooth' });
    };

    return (
        <Stack direction="row" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            {canScrollLeft && (
                <IconButton size="small" onClick={() => scroll('left')} sx={{ p: 0 }}>
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
            )}
            <Box
                ref={scrollRef}
                sx={{
                    overflowX: 'auto',
                    flex: 1,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 'max-content' }}>
                    {children}
                </Stack>
            </Box>
            {canScrollRight && (
                <IconButton size="small" onClick={() => scroll('right')} sx={{ p: 0 }}>
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            )}
        </Stack>
    );
};

export default HorizontalScrollList;
